import { Button } from "@/lib/components/Button/Button";
import Toast from "@/lib/components/Toast";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useCallback, useState } from "react";
import { AnimatedImageProviderType } from "@repo/api/animated-image";
import CreditsText from "@/features/Billing/CreditsText";
import { useIsFeatureEnabled } from "@/lib/feature-flags";
import { useTeamQuery } from "@/lib/queries/team-queries";
import { useParams } from "react-router";
import { useCreditsForAnimation } from "@/features/Billing/billing-hooks";
import { IconPhotoPlus, IconSparkles, IconWaveSine } from "@tabler/icons-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { SuggestAnimationsDialog } from "./SuggestAnimationsDialog";
import { PushInWarningDialog } from "./PushInWarningDialog";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { useBeatSyncsQuery } from "@/lib/queries/beat-sync-queries";
import BeatSyncDialog from "../MusicTab/BeatSyncDialog/BeatSyncDialog";
import { Dot } from "@/lib/components/Badge";
import { useIsSyncedToBeat } from "../useIsSyncedToBeat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { filterTimelineItemsToAnimate } from "@/lib/stores/editor-store/editor-state";
import { cn } from "@/lib/utils";

type ImagesBottomBarProps = {
  isVisible: boolean;
};
const EMPTY_ARRAY: never[] = [];
const MODEL_OPTIONS: Array<{
  value: AnimatedImageProviderType;
  label: string;
}> = [
  { value: "amplifiles/perspective-1.0", label: "Perspective 1.0" },
  { value: "amplifiles/perspective-2.0", label: "Perspective 2.0" },
  { value: "amplifiles/perspective-2.1", label: "Perspective 2.1" },
  { value: "amplifiles/perspective-3.0", label: "Perspective 3.0" },
  { value: "amplifiles/perspective-3.1", label: "Perspective 3.1" },
  { value: "amplifiles/perspective-4.0", label: "Perspective 4.0" },
  { value: "dummy/ffmpeg", label: "Dummy" },
];

function useImagesBottomBar() {
  const { teamExternalId } = useParams();
  const { data: team } = useTeamQuery(teamExternalId!);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuggestLoading, setIsSuggestLoading] = useState(false);
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const [showPushInWarningDialog, setShowPushInWarningDialog] = useState(false);

  const animateImages = useEditorStore((state) => state.service.animateImages);
  const suggestAnimations = useEditorStore(
    (state) => state.service.suggestAnimations,
  );
  const updateAnimationForm = useEditorStore(
    (state) => state.service.updateAnimationForm,
  );
  const provider = useEditorStore((state) => state.animationForm.provider);
  const animationForm = useEditorStore((state) => state.animationForm);
  const timelineItems =
    useEditorStore((state) => state.video?.compositionJson.mainSceneTimeline) ??
    EMPTY_ARRAY;
  const setIsImageSelectorDialogOpen = useEditorStore(
    (state) => state.service.setIsImageSelectorDialogOpen,
  );
  const backgroundMusic = useEditorStore(
    (state) => state.video?.compositionJson.backgroundMusic,
  );
  const isFeatureEnabled = useIsFeatureEnabled();

  const [isBeatSyncOpen, setIsBeatSyncOpen] = useState(false);
  const { data: beatSyncs = [] } = useBeatSyncsQuery(
    teamExternalId!,
    backgroundMusic?.externalId,
  );

  const { credits, numSelected } = useCreditsForAnimation();

  const hasBackgroundMusic = backgroundMusic !== undefined;
  const supportsBeatSync = beatSyncs.length > 0 || isFeatureEnabled;
  const isBeatSyncDisabled = !hasBackgroundMusic || !supportsBeatSync;
  const beatSyncTooltip = !hasBackgroundMusic
    ? "Select a background music track with beat sync first."
    : !supportsBeatSync
      ? "The selected background music doesn't support beat sync"
      : null;

  // Recomputes whenever the composition (clip durations) or beat syncs change.
  const isSyncedToBeat = useIsSyncedToBeat(beatSyncs);

  // Check if current image count exceeds organization policy limit
  const maxImagesInVideo = team?.publicSettings?.maxImagesInVideo ?? null;
  const currentImageCount = timelineItems?.length ?? 0;
  const isOverLimit =
    maxImagesInVideo !== null && currentImageCount > maxImagesInVideo;

  const handleSuggestAnimations = useCallback(async () => {
    if (isSuggestLoading) return;

    setIsSuggestLoading(true);
    try {
      await suggestAnimations();
      Toast.info("Animation suggestions applied!");
    } catch (error) {
      console.error(error);
      Toast.error("Failed to suggest animations");
    } finally {
      setIsSuggestLoading(false);
      setShowSuggestDialog(false);
    }
  }, [isSuggestLoading, setShowSuggestDialog, suggestAnimations]);

  const handleSuggestButtonClick = useCallback(() => {
    const itemsToAnimate = filterTimelineItemsToAnimate(
      timelineItems,
      animationForm.items,
    );
    const allArePushIn = Object.values(itemsToAnimate).every(
      (item) => item.promptType === "PUSH_IN",
    );

    if (!allArePushIn) {
      setShowSuggestDialog(true);
    } else {
      handleSuggestAnimations();
    }
  }, [
    animationForm.items,
    timelineItems,
    handleSuggestAnimations,
    setShowSuggestDialog,
  ]);

  const handleAnimateImages = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await animateImages();
    } catch (error) {
      console.error(error);
      Toast.error("Failed to animate images");
    } finally {
      setIsLoading(false);
    }
  }, [animateImages, isLoading]);

  const handleAnimateButtonClick = useCallback(() => {
    if (isLoading) return;

    // Check if more than 3 images are selected and all are PUSH_IN
    const itemsToAnimate = Object.values(
      filterTimelineItemsToAnimate(timelineItems, animationForm.items),
    );
    const hasMoreThanThreeImages = itemsToAnimate.length > 3;
    const allArePushIn = itemsToAnimate.every(
      (animation) => animation.promptType === "PUSH_IN",
    );
    const isAnimatingAllImages = itemsToAnimate.length === timelineItems.length;

    if (hasMoreThanThreeImages && allArePushIn && isAnimatingAllImages) {
      setShowPushInWarningDialog(true);
      AnalyticsApi.logRepeatingAnimationWarningPopupOpened(teamExternalId);
    } else {
      handleAnimateImages();
    }
  }, [
    animationForm.items,
    timelineItems,
    handleAnimateImages,
    isLoading,
    setShowPushInWarningDialog,
    teamExternalId,
  ]);

  const handleProviderChange = useCallback(
    (value: string) => {
      updateAnimationForm((form) => {
        form.provider =
          value === "DEFAULT" ? null : (value as AnimatedImageProviderType);
      });
    },
    [updateAnimationForm],
  );

  const handleWarningContinue = useCallback(() => {
    setShowPushInWarningDialog(false);
    handleAnimateImages();
  }, [handleAnimateImages, setShowPushInWarningDialog]);

  return {
    team,
    isFeatureEnabled,
    provider,
    credits,
    numSelected,
    isLoading,
    isSuggestLoading,
    showSuggestDialog,
    setShowSuggestDialog,
    showPushInWarningDialog,
    setShowPushInWarningDialog,
    handleSuggestButtonClick,
    handleSuggestAnimations,
    handleAnimateButtonClick,
    handleProviderChange,
    handleWarningContinue,
    setIsImageSelectorDialogOpen,
    maxImagesInVideo,
    currentImageCount,
    isOverLimit,
    backgroundMusicExternalId: backgroundMusic?.externalId,
    beatSyncs,
    isBeatSyncOpen,
    setIsBeatSyncOpen,
    supportsBeatSync,
    isBeatSyncDisabled,
    beatSyncTooltip,
    isSyncedToBeat,
  };
}

export default function ImagesBottomBar({ isVisible }: ImagesBottomBarProps) {
  const {
    team,
    isFeatureEnabled,
    provider,
    credits,
    numSelected,
    isLoading,
    isSuggestLoading,
    showSuggestDialog,
    setShowSuggestDialog,
    showPushInWarningDialog,
    setShowPushInWarningDialog,
    handleSuggestButtonClick,
    handleSuggestAnimations,
    handleAnimateButtonClick,
    handleProviderChange,
    handleWarningContinue,
    setIsImageSelectorDialogOpen,
    maxImagesInVideo,
    currentImageCount,
    isOverLimit,
    backgroundMusicExternalId,
    beatSyncs,
    isBeatSyncOpen,
    setIsBeatSyncOpen,
    supportsBeatSync,
    isBeatSyncDisabled,
    beatSyncTooltip,
    isSyncedToBeat,
  } = useImagesBottomBar();

  if (!isVisible) {
    return null;
  }

  const showCredits = team?.billingInfo.showCredits ?? false;
  const creditBalance = team?.billingInfo.creditBalance ?? null;
  const isCreditsVisible = showCredits && creditBalance !== null;

  return (
    <>
      <div className="flex basis-17.5 items-center justify-end gap-3 border-t border-border bg-white px-6">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 grow-0 basis-auto"
            onClick={() => setIsImageSelectorDialogOpen(true)}
          >
            <IconPhotoPlus />
          </Button>

          {numSelected === 0 && supportsBeatSync && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="relative inline-flex">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={isBeatSyncDisabled}
                    onClick={() => setIsBeatSyncOpen(true)}
                    aria-label="Sync video to beat"
                  >
                    <IconWaveSine strokeWidth={2.5} />
                  </Button>
                  {isSyncedToBeat && (
                    <Dot className="absolute -top-px -right-px h-2 w-2" />
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {beatSyncTooltip ?? "Sync video to beat"}
              </TooltipContent>
            </Tooltip>
          )}

          {isFeatureEnabled ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="h-10 w-10 cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8}>
                <DropdownMenuLabel>Animation model</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={provider ?? "DEFAULT"}
                  onValueChange={handleProviderChange}
                >
                  <DropdownMenuRadioItem value="DEFAULT">
                    Default
                  </DropdownMenuRadioItem>
                  {MODEL_OPTIONS.map((option) => (
                    <DropdownMenuRadioItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        <div className="flex flex-1 justify-end gap-4">
          {numSelected > 0 && (
            <Button
              variant="outline"
              onClick={handleSuggestButtonClick}
              isLoading={isSuggestLoading}
              className="pr-5 pl-4"
              disabled={isSuggestLoading || numSelected === 0}
            >
              <div className="flex items-center">
                <IconSparkles size={16} className="mr-2" />
                Suggest animations
              </div>
            </Button>
          )}

          <Tooltip open={isOverLimit}>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={handleAnimateButtonClick}
                  disabled={
                    numSelected < 1 ||
                    isLoading ||
                    isOverLimit ||
                    isSuggestLoading
                  }
                  className={cn("pl-5", isCreditsVisible ? "pr-0" : "pr-5")}
                >
                  <div className="flex items-center">
                    <span>Animate images</span>
                    <CreditsText
                      className="min-w-20"
                      credits={credits}
                      isVisible={isCreditsVisible}
                    />
                  </div>
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Organization policy limits videos to {maxImagesInVideo} images.
                You currently have {currentImageCount} images.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <SuggestAnimationsDialog
        isOpen={showSuggestDialog}
        onOpenChange={setShowSuggestDialog}
        onConfirm={handleSuggestAnimations}
        isLoading={isSuggestLoading}
      />

      <PushInWarningDialog
        isOpen={showPushInWarningDialog}
        onOpenChange={setShowPushInWarningDialog}
        onContinue={handleWarningContinue}
        isLoading={isLoading}
      />

      {backgroundMusicExternalId !== undefined && (
        <BeatSyncDialog
          open={isBeatSyncOpen}
          onOpenChange={setIsBeatSyncOpen}
          trackExternalId={backgroundMusicExternalId}
          beatSyncs={beatSyncs}
        />
      )}
    </>
  );
}
